import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';

import { graphql } from '@/gql';
import { nonFalsey } from '@/utils/nonFalsey';

const ASSISTANT_FILE_PICKER_FILES_QUERY = graphql(`
  query AssistantFilePickerFiles($fileIds: [ID!]!) {
    files(ids: $fileIds) {
      _id
      filename
    }
  }
`);

interface UseAssistantFilePickerProps {
  fileIds: string[];
  onChange: (fileIds: string[]) => void;
}

export const useAssistantFilePicker = ({
  fileIds,
  onChange,
}: UseAssistantFilePickerProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data } = useQuery(ASSISTANT_FILE_PICKER_FILES_QUERY, {
    variables: { fileIds },
    skip: fileIds.length === 0,
    fetchPolicy: 'cache-first',
  });

  const fileNames = useMemo(() => {
    return (
      data?.files
        ?.map((file) => ({
          _id: file?._id ?? '',
          filename: file?.filename ?? '',
        }))
        .filter(nonFalsey) ?? []
    );
  }, [data]);

  const handleAddFile = (fileId: string | null | undefined) => {
    if (fileId && !fileIds.includes(fileId)) {
      onChange([...fileIds, fileId]);
    }
    setDialogOpen(false);
  };

  const handleDelete = (idToDelete: string) => {
    onChange(fileIds.filter((id) => id !== idToDelete));
  };

  return {
    dialogOpen,
    setDialogOpen,
    fileIds,
    fileNames,
    handleAddFile,
    handleDelete,
    handleClose: () => setDialogOpen(false),
  };
};
